import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

interface TrueImpactTooltipProps {
  title: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: string;
  minWidth?: string;
  color?: string;
  className?: string;
  backgroundColor?: string;
  containerDisplay?: React.CSSProperties['display'];
  tooltipContainerClassName?: string;
}

const TooltipContainer = styled.div<{
  maxWidth?: string;
  minWidth?: string;
  color: string;
  backgroundColor: string;
}>`
  position: fixed;
  background: ${({ backgroundColor }) => backgroundColor};
  color: ${({ color }) => color};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 9999;
  pointer-events: auto;
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}`};
  ${({ minWidth }) => minWidth && `min-width: ${minWidth}`};
  word-wrap: break-word;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

  /* Arrow pointing up */
  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${({ backgroundColor }) => backgroundColor};
  }
`;

const TrueImpactTooltip: React.FC<TrueImpactTooltipProps> = ({
  title,
  children,
  maxWidth,
  minWidth,
  className,
  containerDisplay = 'inline-block',
  tooltipContainerClassName,
  color = COLORS.blackBase,
  backgroundColor = COLORS.whiteBase,
}) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 12, // offset for arrow + spacing
        left: rect.left + rect.width / 2,
      });
    }
  }, [visible]);

  return (
    <>
      <div
        ref={targetRef}
        onMouseEnter={() => {
          if (!dismissed) {
            setVisible(true);
          }
        }}
        onMouseLeave={() => {
          setVisible(false);
          setDismissed(false);
        }}
        onClick={() => {
          setVisible(false);
          setDismissed(true);
        }}
        style={{ display: containerDisplay }}
        className={className}
      >
        {children}
      </div>
      {visible &&
        createPortal(
          <TooltipContainer
            maxWidth={maxWidth}
            minWidth={minWidth}
            color={color}
            backgroundColor={backgroundColor}
            className={tooltipContainerClassName}
            style={{
              top: coords.top,
              left: coords.left,
              transform: 'translateX(-50%)',
            }}
          >
            {title}
          </TooltipContainer>,
          document.body,
        )}
    </>
  );
};

export default TrueImpactTooltip;
